// composables/useCloudinary.ts

import { Cloudinary } from '@cloudinary/url-gen';
import { fill, fit, scale, crop, pad } from '@cloudinary/url-gen/actions/resize';
// import { autoGravity } from '@cloudinary/url-gen/qualifiers/gravity';
import { dpr } from '@cloudinary/url-gen/actions/delivery';
import { QualityTypes, CropMode, ImageFormatType } from '@cloudinary/url-gen/types/types';
import { ICompassGravity } from '@cloudinary/transformation-builder-sdk/qualifiers/gravity/compassGravity/CompassGravity';
import { CloudinaryAsset } from '@solo-stove/types/contentstack';
import { Breakpoint } from '~/configs/breakpoints';

export type Transform =
  | 'fill'
  | 'fit'
  | 'extend'
  | 'scale'
  | 'crop'
  | 'rotate'
  | 'flip'
  | 'mirror'
  | 'border'
  | 'background'
  | 'pad';

export type ImageOptions = {
  width?: number | 'auto';
  height?: number | 'auto';
  crop?: CropMode;
  format?: ImageFormatType;
  transform?: Transform;
  dpr?: number;
  quality?: QualityTypes | number;
  gravity?: ICompassGravity;
};

export type VideoOptions = {
  width?: number | 'auto';
  height?: number | 'auto';
  crop?: CropMode;
  transform?: Transform;
  dpr?: number;
  thumbnail?: boolean;
  quality?: QualityTypes | number;
  gravity?: ICompassGravity;
};

export type ProgressiveImageUrl = {
  quality: number | QualityTypes;
  url: string;
};

export type CldGravity = ICompassGravity;

export type SourceSet = {
  cloudinary_asset: CloudinaryAsset[];
  transform?: ImageOptions;
  poster?: {
    cloudinary_asset: CloudinaryAsset[];
    transform?: ImageOptions;
  };
  breakpoint: Breakpoint;
};

export const useCloudinary = (enableLogging = false) => {
  const cld = new Cloudinary({
    cloud: {
      cloudName: 'solostove',
    },
    url: {
      analytics: false,
      secure: true,
      secureDistribution: 'content.solostove.com',
      privateCdn: true,
    },
  });

  const qualityArray: (QualityTypes | number)[] = [1, 'auto', 'auto:best'];
  const generateImageUrl = (publicId: string, options?: ImageOptions) => {
    if (enableLogging) console.time('generateImageUrl');
    const image = generateCldImage(publicId, options);
    const quality = options?.quality || 'auto';
    image.delivery(dpr('auto'));
    image.quality(quality);
    const url = image.toURL();
    if (enableLogging) console.timeEnd('generateImageUrl');
    return url;
  };

  const generateProgressiveImageUrls = (
    publicId: string,
    options?: ImageOptions,
    qualityArrayOverride?: typeof qualityArray
  ): ProgressiveImageUrl[] => {
    if (enableLogging) console.time('generateProgressiveImageUrls');
    const currQualityArray = qualityArrayOverride || qualityArray;

    const imagesArray = currQualityArray.map((qualityLevel) => ({
      quality: qualityLevel,
      url: generateCldImage(publicId, options).quality(qualityLevel).delivery(dpr('auto')).toURL(),
    }));

    if (enableLogging) console.timeEnd('generateProgressiveImageUrls');
    return imagesArray;
  };

  const generateCldImage = (publicId: string, options?: ImageOptions) => {
    if (enableLogging) console.time('generateCldImage');
    const image = cld.image(publicId);

    if (options) {
      const resizeAction = transformToResizeMode(options?.transform || 'fill');
      const resizeWidth = typeof options.width === 'number' ? Math.round(options.width) : options.width;
      const resizeHeight = typeof options.height === 'number' ? Math.round(options.height) : options.height;
      if (resizeWidth) resizeAction.width(resizeWidth);
      if (resizeHeight) resizeAction.height(resizeHeight);
      // @ts-ignore-next-line -- .gravity is not in type
      // resizeAction.gravity(options?.gravity || autoGravity());
      if (options?.gravity) resizeAction.gravity(options.gravity);
      if (resizeWidth || resizeHeight) image.resize(resizeAction);
    }

    const imageFormat = options?.format || 'auto';
    image.format(imageFormat);

    if (enableLogging) console.timeEnd('generateCldImage');
    return image;
  };

  const generateCldVideo = (publicId: string, options?: VideoOptions) => {
    if (enableLogging) console.time('generateCldVideo');
    const video = cld.video(publicId);
    const resizeAction = transformToResizeMode(options?.transform || 'fill');
    if (typeof options?.width === 'number') resizeAction.width(Math.round(options.width));
    if (typeof options?.height === 'number') resizeAction.height(Math.round(options.height));
    // @ts-ignore-next-line -- .gravity is not in type
    // resizeAction.gravity(options?.gravity || autoGravity());
    if (options?.gravity) resizeAction.gravity(options.gravity);
    video.resize(resizeAction);

    if (enableLogging) console.timeEnd('generateCldVideo');
    return video;
  };

  const generateVideoUrl = (publicId: string, options?: VideoOptions) => {
    if (enableLogging) console.time('generateVideoUrl');
    const video = generateCldVideo(publicId, options);
    const quality = options?.quality || 'auto';
    let isThumbnail = false;
    if (options?.thumbnail) isThumbnail = options.thumbnail;
    video.delivery(dpr('auto'));
    if (!isThumbnail) {
      video.quality(quality).format('auto:video');
    } else {
      video.quality(quality).format('auto');
    }
    const url = video.toURL();
    if (enableLogging) console.timeEnd('generateVideoUrl');
    return url;
  };

  const transformToResizeMode = (transform: Transform) => {
    switch (transform) {
      case 'fill':
        return fill();
      case 'fit':
        return fit();
      case 'scale':
        return scale();
      case 'crop':
        return crop();
      case 'pad':
        return pad();
      default:
        return fill(); // Default or fallback to 'fill'
    }
  };

  return {
    generateImageUrl,
    generateProgressiveImageUrls,
    generateVideoUrl,
    generateCldImage,
    generateCldVideo,
    qualityArray,
  };
};
